/**
 * Host → org resolution for Vet Media association portals (SCAV + VMAE demos).
 */

const VMAE_DOMAIN_SUFFIX = '.veterinarymedia.ai';
const VMAE_DOMAIN_SUFFIX_LOCAL = '.veterinarymedia.ai.local';
const RESERVED_SUBDOMAINS = new Set(['www', 'app', 'api', 'admin', 'dev']);

/** Apex / shared SPA hosts that serve Veterinary Media product branding. */
const VETMEDIA_PRODUCT_HOSTS = new Set([
  'vetmedia.ai',
  'www.vetmedia.ai',
  'app.vetmedia.ai',
  'veterinarymedia.ai',
  'www.veterinarymedia.ai',
  'app.veterinarymedia.ai',
  'vetmediagroup.com',
  'www.vetmediagroup.com',
  'app.vetmediagroup.com',
]);

export function getAssociationSlugFromHost(hostname = window.location.hostname) {
  const host = hostname.toLowerCase();
  let suffix = null;
  if (host.endsWith(VMAE_DOMAIN_SUFFIX)) {
    suffix = VMAE_DOMAIN_SUFFIX;
  } else if (host.endsWith(VMAE_DOMAIN_SUFFIX_LOCAL)) {
    suffix = VMAE_DOMAIN_SUFFIX_LOCAL;
  } else {
    return null;
  }

  const sub = host.slice(0, -suffix.length);
  if (!sub || RESERVED_SUBDOMAINS.has(sub)) {
    return null;
  }
  return sub;
}

export function isVmaeAssociationHost(hostname = window.location.hostname) {
  return Boolean(getAssociationSlugFromHost(hostname));
}

/**
 * True when this hostname is a Veterinary Media product surface
 * (apex demo, app shell, or association subdomain) — not ADI core.
 */
export function isVetMediaProductHost(hostname = window.location.hostname) {
  const host = (hostname || '').toLowerCase();
  if (!host) return false;
  if (VETMEDIA_PRODUCT_HOSTS.has(host)) return true;
  if (host.endsWith(VMAE_DOMAIN_SUFFIX) || host.endsWith(VMAE_DOMAIN_SUFFIX_LOCAL)) {
    return true;
  }
  return host.includes('vetmediagroup.com') || host.includes('vetmedia.ai') || host.includes('scav.org');
}

export function getVmaeDemoOrgSlug(hostname = window.location.hostname) {
  const slug = getAssociationSlugFromHost(hostname);
  return slug ? `vmae-demo-${slug}` : null;
}

export function isVmaeDemoOrgSlug(orgSlug) {
  return typeof orgSlug === 'string' && orgSlug.startsWith('vmae-demo-');
}

export function isLearningPortalOrg(orgSlug) {
  if (!orgSlug) {
    return false;
  }
  return ['colibri-media-group', 'scav'].includes(orgSlug) || isVmaeDemoOrgSlug(orgSlug);
}

/**
 * Post-login / home landing for LMS surfaces.
 * Vet Media association orgs open Vet Consult (agent tab), not the course catalog.
 */
export function learningHomeLocation(orgSlug) {
  if (isLearningPortalOrg(orgSlug)) {
    return { path: '/learning', query: { tab: 'agent' } };
  }
  return { path: '/learning' };
}

export function resolveOrgSlugFromHost(hostname = window.location.hostname) {
  const host = hostname.toLowerCase();
  if (
    host.includes('vetmediagroup.com')
    || host.includes('scav.org')
    || host === 'veterinarymedia.ai'
    || host === 'www.veterinarymedia.ai'
    || host === 'app.veterinarymedia.ai'
  ) {
    return 'colibri-media-group';
  }
  return getVmaeDemoOrgSlug(host);
}

export function loginPathForOrg(orgSlug) {
  return orgSlug ? `/login?org=${encodeURIComponent(orgSlug)}` : '/login';
}

export function registerPathForOrg(orgSlug) {
  return orgSlug ? `/register?org=${encodeURIComponent(orgSlug)}` : '/register';
}
